import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { TeamResponse } from '@/services/teamService';
import type { User } from '@/types/auth';

type Props = {
  salespeople: User[];
  teams: TeamResponse[];
  onAssign: (userId: string, teamId: string) => void;
  onDelete: (user: User) => void;
};

export default function UnassignedMembers({ salespeople, teams, onAssign, onDelete }: Props) {
  const { t } = useTranslation();

  if (salespeople.length === 0) return null;

  return (
    <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 px-5 py-4">
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 size-5 shrink-0 text-amber-600 dark:text-amber-400" />
        <div className="flex-1">
          <p className="text-sm font-medium text-foreground">
            {t('teamManagement.unassignedBanner', {
              count: salespeople.length,
              defaultValue: '{{count}} vendedores sin equipo asignado',
            })}
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            {salespeople.map((sp) => (
              <div key={sp.id} className="flex items-center gap-1.5 rounded-md border border-border bg-card px-2.5 py-1.5 text-sm">
                <span className="font-medium text-foreground">{sp.firstName} {sp.lastName}</span>
                {teams.length > 0 && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="xs" className="text-primary">
                        {t('teamManagement.assign', 'Asignar')}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {teams.map((team) => (
                        <DropdownMenuItem key={team.id} onClick={() => onAssign(sp.id, team.id)}>
                          {team.name}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
                <Button variant="ghost" size="xs" onClick={() => onDelete(sp)} className="text-muted-foreground hover:text-destructive">
                  ×
                </Button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
